/**
 * 현장 맵 도면 모델 — offline-manage master-console 의 map.js(buildFloor/computeBase)
 * 를 그대로 옮긴 순수 함수. export 형식(parkingLotLevels[].mapData)을 받아
 * 캔버스에 그릴 선·폴리곤·라벨·CCTV 마커와 fit 변환을 만든다.
 */

export type Pt = [number, number];

export interface SiteMapCamera {
  id: string;
  name: string;
  ip: string;
  x: number;
  y: number;
  kind: 'light' | 'warning';
}

export interface SiteMapFloorModel {
  geom: { poly: boolean; pts: Pt[] }[];
  labels: { text: string; x: number; y: number }[];
  cameras: SiteMapCamera[];
  /** CCTV 이름 → 담당 주차면 꼭짓점 목록 */
  slotsByCam: Map<string, Pt[][]>;
}

export interface SiteMapLevel {
  name: string;
  mapData: Record<string, unknown>;
}

/** 구조 키 — 라벨 후보에서 제외(map.js STRUCTURAL_KEYS 와 동일). */
const STRUCTURAL_KEYS = new Set([
  'outLines', 'innerLines', 'arrows', 'zones', 'columns', 'parkingLocations',
  'cctvs', 'emergencyBells', 'sensors', 'chargers', 'guideBoards', 'elevators',
  'entrances', 'onePassReaders', 'occupancyLights', 'lights', 'relationships',
  'metadata', 'name', 'floorName', 'floorOrder', 'createdAt', 'author', 'description',
]);

/** position 은 {x,y}(에디터) 또는 [x,y](구형 fixture) 둘 다 온다. */
export function xy(p: unknown): Pt | null {
  if (Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number') {
    return [p[0], p[1]];
  }
  if (p && typeof p === 'object') {
    const o = p as { x?: unknown; y?: unknown };
    if (typeof o.x === 'number' && typeof o.y === 'number') return [o.x, o.y];
  }
  return null;
}

export function centroid(pts: unknown): Pt | null {
  let sx = 0, sy = 0, n = 0;
  if (Array.isArray(pts)) {
    pts.forEach((p) => {
      const v = xy(p);
      if (v) { sx += v[0]; sy += v[1]; n += 1; }
    });
  }
  return n ? [sx / n, sy / n] : null;
}

function toPts(points: unknown): Pt[] {
  if (!Array.isArray(points)) return [];
  return points.map(xy).filter((v): v is Pt => v !== null);
}

type AnyObj = Record<string, any>;

export function buildFloorModel(md: Record<string, unknown> | undefined | null): SiteMapFloorModel {
  const m: AnyObj = md || {};
  const geom: SiteMapFloorModel['geom'] = [];
  const pushPoly = (arr: unknown, poly: boolean) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((o: AnyObj) => {
      const pts = toPts(o?.points);
      if (pts.length >= 2) geom.push({ poly, pts });
    });
  };
  pushPoly(m.outLines, false);
  pushPoly(m.innerLines, false);
  pushPoly(m.arrows, false);
  pushPoly(m.zones, true);
  pushPoly(m.columns, true);
  pushPoly(m.parkingLocations, true);

  const labels: SiteMapFloorModel['labels'] = [];
  const addLabel = (text: unknown, pts: unknown, pos: unknown, size: AnyObj | undefined) => {
    const t = (text == null ? '' : String(text)).trim();
    if (!t) return;
    let c = centroid(pts);
    if (!c) {
      const v = xy(pos);
      if (v) c = [v[0] + (size?.width ? size.width / 2 : 0), v[1] + (size?.height ? size.height / 2 : 0)];
    }
    if (c) labels.push({ text: t, x: c[0], y: c[1] });
  };
  (Array.isArray(m.zones) ? m.zones : []).forEach((z: AnyObj) => addLabel(z.name || z.zoneName, z.points, z.position, z.size));
  Object.keys(m).forEach((key) => {
    if (STRUCTURAL_KEYS.has(key)) return;
    const arr = m[key];
    if (!Array.isArray(arr)) return;
    arr.forEach((o: AnyObj) => { if (o && typeof o === 'object') addLabel(o.name, o.points, o.position, o.size); });
  });

  const cameras: SiteMapCamera[] = [];
  const c: AnyObj = m.cctvs || {};
  const addCams = (arr: unknown, kind: SiteMapCamera['kind']) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((cam: AnyObj) => {
      const v = xy(cam?.position);
      if (v) cameras.push({ id: String(cam.id ?? ''), name: String(cam.name || cam.id || ''), ip: String(cam.ipAddress || ''), x: v[0], y: v[1], kind });
    });
  };
  addCams(c.lightCctvs, 'light');
  addCams(c.warningCctvs, 'warning');

  // CCTV↔주차면 관계. 링크 값은 맵 세대에 따라 주차면 '이름'(신형)일 수도 'id'(구형)
  // 일 수도 있어 둘 다 받는다. 방향도 두 가지 — CCTV 쪽 *parkingLocation*Ids 와
  // 주차면 쪽 *cctv*Id(s) — 어느 쪽에 적혀 있든 같은 관계로 모은다.
  const slotPts = new Map<string, Pt[]>();
  const parkingLocations: AnyObj[] = Array.isArray(m.parkingLocations) ? m.parkingLocations : [];
  parkingLocations.forEach((o) => {
    const pts = toPts(o?.points);
    if (pts.length < 2) return;
    const nm = String(o.name ?? '');
    const id = String(o.id ?? '');
    if (nm) slotPts.set(nm, pts);
    if (id && !slotPts.has(id)) slotPts.set(id, pts);
  });
  const camNameByKey = new Map<string, string>();
  cameras.forEach((cam) => {
    camNameByKey.set(cam.name, cam.name);
    if (cam.id && !camNameByKey.has(cam.id)) camNameByKey.set(cam.id, cam.name);
  });
  const slotsByCam = new Map<string, Pt[][]>();
  const link = (camName: string, pts: Pt[]) => {
    const list = slotsByCam.get(camName) || [];
    if (!list.includes(pts)) list.push(pts);
    slotsByCam.set(camName, list);
  };
  const idsOf = (v: unknown): string[] =>
    Array.isArray(v) ? v.map(String) : typeof v === 'string' ? v.split(',').map((x) => x.trim()).filter(Boolean) : [];
  const addFromCams = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((cam: AnyObj) => {
      const nm = String(cam?.name || cam?.id || '');
      if (!nm) return;
      Object.keys(cam).forEach((key) => {
        if (!/parkinglocation.*ids?$/i.test(key)) return;
        idsOf(cam[key]).forEach((k) => { const pts = slotPts.get(k); if (pts) link(nm, pts); });
      });
    });
  };
  addFromCams(c.lightCctvs);
  addFromCams(c.warningCctvs);
  parkingLocations.forEach((o) => {
    const pts = toPts(o?.points);
    if (pts.length < 2) return;
    Object.keys(o).forEach((key) => {
      if (!/cctv.*ids?$/i.test(key)) return;
      idsOf(o[key]).forEach((k) => { const nm = camNameByKey.get(k); if (nm) link(nm, pts); });
    });
  });

  return { geom, labels, cameras, slotsByCam };
}

export interface FitBase { scale: number; ox: number; oy: number }

/** 전체 bbox 를 캔버스에 맞추는 기본 변환(map.js computeBase). */
export function computeBase(model: SiteMapFloorModel, w: number, h: number, pad = 28): FitBase | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const consider = (x: number, y: number) => {
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  };
  model.geom.forEach((g) => g.pts.forEach((p) => consider(p[0], p[1])));
  model.cameras.forEach((cam) => consider(cam.x, cam.y));
  model.labels.forEach((l) => consider(l.x, l.y));
  if (!isFinite(minX)) return null;
  const spanX = Math.max(1, maxX - minX), spanY = Math.max(1, maxY - minY);
  const scale = Math.min((w - pad * 2) / spanX, (h - pad * 2) / spanY);
  const ox = pad + (w - pad * 2 - spanX * scale) / 2 - minX * scale;
  const oy = pad + (h - pad * 2 - spanY * scale) / 2 - minY * scale;
  return { scale, ox, oy };
}

/** 모든 층의 CCTV 를 이름 기준 중복 없이 모은다(층 이름 포함). */
export function collectCameras(levels: SiteMapLevel[]): (SiteMapCamera & { floor: string })[] {
  const seen = new Set<string>();
  const out: (SiteMapCamera & { floor: string })[] = [];
  levels.forEach((lvl) => {
    buildFloorModel(lvl.mapData).cameras.forEach((cam) => {
      if (!cam.name || seen.has(cam.name)) return;
      seen.add(cam.name);
      out.push({ ...cam, floor: lvl.name });
    });
  });
  return out;
}
